import React, { createContext, useContext, useState } from 'react';

interface Proposal {
  id: string;
  title: string;
  description: string;
  status: 'active' | 'passed' | 'failed' | 'pending';
  votesFor: number;
  votesAgainst: number;
  endDate: Date;
}

interface GovernanceContextType {
  proposals: Proposal[];
  userVotes: Record<string, 'for' | 'against' | null>;
  vote: (proposalId: string, vote: 'for' | 'against') => Promise<void>;
  createProposal: (title: string, description: string) => Promise<void>;
}

const GovernanceContext = createContext<GovernanceContextType | undefined>(undefined);

export const useGovernance = () => {
  const context = useContext(GovernanceContext);
  if (context === undefined) {
    throw new Error('useGovernance must be used within a GovernanceProvider');
  }
  return context;
};

export const GovernanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [proposals, setProposals] = useState<Proposal[]>([
    {
      id: '1',
      title: 'Acquire Thunder Bay for $2.5M',
      description: 'Purchase champion thoroughbred Thunder Bay to strengthen our racing portfolio.',
      status: 'active',
      votesFor: 156,
      votesAgainst: 23,
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    },
    {
      id: '2',
      title: 'Increase SUI Staking Allocation',
      description: 'Move 10% of USDC reserves to SUI staking for higher yields.',
      status: 'active',
      votesFor: 89,
      votesAgainst: 45,
      endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
    }
  ]);

  const [userVotes, setUserVotes] = useState<Record<string, 'for' | 'against' | null>>({});

  const vote = async (proposalId: string, vote: 'for' | 'against') => {
    // Simulate voting
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setUserVotes(prev => ({ ...prev, [proposalId]: vote }));
    
    setProposals(prev => prev.map(proposal => 
      proposal.id === proposalId 
        ? {
            ...proposal,
            votesFor: vote === 'for' ? proposal.votesFor + 1 : proposal.votesFor,
            votesAgainst: vote === 'against' ? proposal.votesAgainst + 1 : proposal.votesAgainst
          }
        : proposal
    ));
  };

  const createProposal = async (title: string, description: string) => {
    // Simulate proposal creation
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newProposal: Proposal = {
      id: Date.now().toString(),
      title,
      description,
      status: 'pending',
      votesFor: 0,
      votesAgainst: 0,
      endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
    };
    
    setProposals(prev => [newProposal, ...prev]);
  };

  return (
    <GovernanceContext.Provider value={{
      proposals,
      userVotes,
      vote,
      createProposal
    }}>
      {children}
    </GovernanceContext.Provider>
  );
};
